import React, { ReactElement, memo } from 'react';
import { Button, Grid, Select, Tooltip, Paragraph, Checkbox, Icon, Divider } from '@dexma/ui-components';
import withLoading from 'adapter/in/common/hoc/withLoading';
import { Option, isSome } from 'fp-ts/Option';
import { DashboardUserGroup } from 'domain/entity/dashboard';
import i18n from 'asset/translation';
import {
    StyledUpperRow,
    StyledCenteredButton,
    StyledDividerWithMargin,
    StyledMiddleBlock,
    StyledUserGroupRow,
    StyledNotSharedMessage,
    StyledLowerRow,
    StyledDisclaimerMessage,
    StyledPublicDashboard,
    StyledDashboarShareModal,
    StyledUpperRowLabel,
} from 'adapter/in/dashboards/pages/Modals/ShareModal/style';
import ServerErrorAlert from 'adapter/in/dashboards/components/ServerErrorAlert';
import gtm from 'adapter/in/dashboards/config/gtm';
import { SHARE_OPTIONS, ShareSelectorOption } from 'adapter/in/dashboards/containers/Modals/ShareDashboardModal/useOnShareDashboardModal';
import { ErrorData } from 'domain/types';

export type DashboardShareModalProps = ShareModalCommonProps & {
    showModal: boolean;
    handleCloseModal: () => void;
    error: Option<ErrorData>;
    data: Option<DashboardUserGroup[]>;
    isLoading: boolean;
    errorAction: () => void;
};

export type ShareModalCommonProps = {
    userGroupsWithAccess: DashboardUserGroup[];
    isLoadingUpdate: boolean;
    handleAddUserGroupToList: () => void;
    handleUserGroupSelectChange: (multiSelectorValue: string[]) => void;
    selectedUserGroupsToAggreggate: string[] | undefined;
    handleSelectAccessType: (selectorValue: ShareSelectorOption) => void;
    selectedAccessType: ShareSelectorOption;
    handleCheckMakePublic: () => void;
    isDashboardPublic: boolean;
    hasDisclaimer: boolean;
    hasUpdateError: boolean;
    handleSaveShareChanges: () => void;
    handleOnChangeAccessType: (newAccess: string, groupId: string) => void;
    hasUpdateSuccess: boolean;
    isPinned?: boolean;
    handleCancel: () => void;
    tryAgain?: () => void;
};

type ShareModalContentProps = Omit<
    ShareModalCommonProps,
    | 'handleCheckMakePublic'
    | 'isDashboardPublic'
    | 'isPinned'
    | 'handleSaveShareChanges'
    | 'handleCancel'
    | 'isLoadingUpdate'
    | 'hasUpdateSuccess'
> & { data: DashboardUserGroup[] };
const DashboardShareModal = memo(
    (props: DashboardShareModalProps): ReactElement => {
        const {
            userGroupsWithAccess,
            data,
            handleUserGroupSelectChange,
            handleAddUserGroupToList,
            hasUpdateError,
            selectedUserGroupsToAggreggate,
            handleSelectAccessType,
            selectedAccessType,
            handleCheckMakePublic,
            isDashboardPublic,
            isPinned,
            hasDisclaimer,
            handleSaveShareChanges,
            handleOnChangeAccessType,
            handleCancel,
            isLoadingUpdate,
            tryAgain,
            hasUpdateSuccess,
            showModal,
            handleCloseModal,
            isLoading,
            error,
            errorAction,
        } = props;

        return (
            <StyledDashboarShareModal
                width={700}
                open={showModal}
                closable={isSome(error)}
                onCancel={handleCloseModal}
                title={i18n.t('dashboards.share.title')}
                body={
                    <ShareModalContent
                        errorAction={errorAction}
                        data={data}
                        isLoading={isLoading}
                        error={error}
                        handleUserGroupSelectChange={handleUserGroupSelectChange}
                        handleAddUserGroupToList={handleAddUserGroupToList}
                        hasUpdateError={hasUpdateError}
                        selectedUserGroupsToAggreggate={selectedUserGroupsToAggreggate}
                        handleSelectAccessType={handleSelectAccessType}
                        selectedAccessType={selectedAccessType}
                        hasDisclaimer={hasDisclaimer}
                        handleOnChangeAccessType={handleOnChangeAccessType}
                        tryAgain={tryAgain}
                        userGroupsWithAccess={userGroupsWithAccess}
                    />
                }
                footer={
                    <div style={{ width: '100%' }}>
                        <StyledLowerRow>
                            <StyledPublicDashboard data-testid='make-dashboard-public-toggle'>
                                {isPinned ? (
                                    <Tooltip style={{ display: 'flex' }} position='top' title={i18n.t('dashboards.pinned.tooltip.share')}>
                                        <Checkbox disabled checked={isDashboardPublic} onChange={handleCheckMakePublic} />
                                        <Paragraph style={{ marginLeft: '8px' }}>{i18n.t('dashboards.share.makePublic')}</Paragraph>
                                    </Tooltip>
                                ) : (
                                    <>
                                        <Checkbox checked={isDashboardPublic} onChange={handleCheckMakePublic} />
                                        <Paragraph style={{ marginLeft: '8px' }}>{i18n.t('dashboards.share.makePublic')}</Paragraph>
                                    </>
                                )}
                                <Tooltip style={{ display: 'flex' }} position='right' title={i18n.t('dashboards.share.makePublicTooltip')}>
                                    <Icon
                                        style={{ marginLeft: '2px' }}
                                        size={14}
                                        name='circle_info_outline'
                                        ariaLabel={i18n.t('dashboards.share.infoIconAriaLabel')}
                                    />
                                </Tooltip>
                            </StyledPublicDashboard>

                            <div style={{ display: 'flex' }}>
                                <Button
                                    kind='iconTextButton'
                                    style={{ marginRight: '16px' }}
                                    size='large'
                                    data-testid='save-button'
                                    dataId={gtm.dashboardAccess.actions.save}
                                    iconBefore={hasUpdateSuccess ? 'check' : ''}
                                    onClick={handleSaveShareChanges}
                                    isLoading={isLoadingUpdate}
                                    text={i18n.t('dashboards.share.save')}
                                />
                                <Button
                                    size='large'
                                    variant='secondary'
                                    data-testid='cancel-button'
                                    dataId={gtm.dashboardAccess.actions.cancelSave}
                                    onClick={handleCancel}
                                    text={i18n.t('dashboards.share.cancel')}
                                />
                            </div>
                        </StyledLowerRow>
                    </div>
                }
                closeModalButtonAriaLabel={i18n.t('common.ariaLabel.closeModalIcon')}
            />
        );
    }
);

const ShareModalContent = withLoading((props: ShareModalContentProps) => {
    const {
        userGroupsWithAccess: sortedAccessGroups,
        data: aggreggableUserGroups,
        handleUserGroupSelectChange,
        handleAddUserGroupToList,
        hasUpdateError,
        selectedUserGroupsToAggreggate,
        handleSelectAccessType,
        selectedAccessType,
        hasDisclaimer,
        handleOnChangeAccessType,
        tryAgain,
    } = props;

    return (
        <>
            <Grid>
                {hasUpdateError && tryAgain && <ServerErrorAlert tryAgain={tryAgain} dataId={gtm.dashboardAccess.actions.tryAgain} />}
                <StyledUpperRowLabel>{i18n.t('dashboards.share.userGroups')}</StyledUpperRowLabel>
                <StyledUpperRow data-testid='aggreggable-usergroups-option'>
                    <div style={{ width: '60%', marginRight: '8px' }}>
                        <Select
                            onChange={handleUserGroupSelectChange}
                            mode='multiple'
                            defaultValues={selectedUserGroupsToAggreggate}
                            options={aggreggableUserGroups.map((userGroup) => ({
                                label: userGroup.name,
                                value: userGroup.id,
                                color: 'gray300',
                            }))}
                            showOptionsAriaLabel={i18n.t('common.ariaLabel.showSelectOptions')}
                            hideOptionsAriaLabel={i18n.t('common.ariaLabel.hideSelectOptions')}
                        />
                    </div>
                    <div style={{ width: '20%', marginRight: '8px' }}>
                        <Select
                            defaultValues={[selectedAccessType]}
                            onChange={handleSelectAccessType}
                            options={[
                                {
                                    label: i18n.t('dashboards.share.view'),
                                    value: SHARE_OPTIONS.view,
                                },
                                {
                                    label: i18n.t('dashboards.share.edit'),
                                    value: SHARE_OPTIONS.edit,
                                },
                            ]}
                            showOptionsAriaLabel={i18n.t('common.ariaLabel.showSelectOptions')}
                            hideOptionsAriaLabel={i18n.t('common.ariaLabel.hideSelectOptions')}
                        />
                    </div>
                    <StyledCenteredButton>
                        <Button
                            data-testid='add-user-group-button'
                            isDisabled={aggreggableUserGroups.length === 0 || selectedUserGroupsToAggreggate?.length === 0}
                            onClick={handleAddUserGroupToList}
                            text={i18n.t('dashboards.share.add')}
                            color='primary'
                            isExpanded
                        />
                    </StyledCenteredButton>
                </StyledUpperRow>
            </Grid>
            <Divider />
            <StyledMiddleBlock>
                <UserGroupList userGroupsWithAccess={sortedAccessGroups} handleOnChangeAccessType={handleOnChangeAccessType} />
            </StyledMiddleBlock>
            {hasDisclaimer && <DisclaimerMessage />}
        </>
    );
});

type UserGroupListProps = {
    userGroupsWithAccess: DashboardUserGroup[];
    handleOnChangeAccessType: (newAccess: string, groupId: string) => void;
};
const UserGroupList = (props: UserGroupListProps) => {
    const { userGroupsWithAccess, handleOnChangeAccessType } = props;

    const alphabetically = (groupA: DashboardUserGroup, groupB: DashboardUserGroup) => {
        if (groupA.name < groupB.name) {
            return -1;
        }
        return 1;
    };

    const mapLabel = (value: string) => {
        switch (value) {
            case SHARE_OPTIONS.view:
                return i18n.t('dashboards.share.view');
            case SHARE_OPTIONS.remove:
                return i18n.t('dashboards.share.remove');
            case SHARE_OPTIONS.edit:
            default:
                return i18n.t('dashboards.share.edit');
        }
    };
    const rows =
        userGroupsWithAccess.length > 0 ? (
            userGroupsWithAccess.sort(alphabetically).map((group: DashboardUserGroup) => (
                <React.Fragment key={group.id}>
                    <StyledUserGroupRow>
                        <div>{group.name}</div>
                        <div style={{ width: '25%' }}>
                            <Select
                                onChange={(e: ShareSelectorOption) => {
                                    handleOnChangeAccessType(e, group.id);
                                }}
                                defaultValues={[group.level]}
                                options={[
                                    { label: mapLabel(SHARE_OPTIONS.view), value: SHARE_OPTIONS.view },
                                    { label: mapLabel(SHARE_OPTIONS.edit), value: SHARE_OPTIONS.edit },
                                    { label: mapLabel(SHARE_OPTIONS.remove), value: SHARE_OPTIONS.remove },
                                ]}
                                showOptionsAriaLabel={i18n.t('common.ariaLabel.showSelectOptions')}
                                hideOptionsAriaLabel={i18n.t('common.ariaLabel.hideSelectOptions')}
                            />
                        </div>
                    </StyledUserGroupRow>
                    <Divider />
                </React.Fragment>
            ))
        ) : (
            <>
                <StyledNotSharedMessage>{i18n.t('dashboards.share.notShared')}</StyledNotSharedMessage>
                <StyledDividerWithMargin />
            </>
        );

    return <Grid data-testid='user-group-list'>{rows}</Grid>;
};

const DisclaimerMessage = () => (
    <StyledDisclaimerMessage>
        <div style={{ padding: '0px 12px' }}>
            <Icon size={20} style={{ marginRight: '8px', marginBottom: '-4px' }} name='warning' ariaLabel='' />
            {i18n.t('dashboards.share.editDisclaimer')}
        </div>
    </StyledDisclaimerMessage>
);

export default DashboardShareModal;
